import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import validate from 'validate.js';
import { makeStyles } from '@mui/styles';
import { Typography, Button, Card, CardHeader, CardContent, CardActions, Grid, Divider, TextField, colors } from '@mui/material';
import { enqueueSnackbar } from 'actions';
import { GetTeamAssign, GetTeamAssignDetails, SetFormStateEricsson, SetFormStateNokia, ResetAllocation, SaveAllocation } from '../../actions/ManagementActions';

const useStyles = makeStyles((theme) => ({
	root: {
		padding: theme.spacing(3),
	},
	container: {
		marginTop: theme.spacing(3),
	},
	tabs: {
		marginTop: theme.spacing(3),
	},
	content: {
		marginTop: theme.spacing(3),
	},
	save: {
		marginLeft: 'auto',
		color: theme.palette.white,
		backgroundColor: colors.green[600],
		'&:hover': {
			backgroundColor: colors.green[900],
		},
	},
	reset: {
		marginLeft: 'auto',
		color: theme.palette.white,
		backgroundColor: colors.blue[600],
		'&:hover': {
			backgroundColor: colors.blue[900],
		},
	}
}));

const schema = {
	monday: {
		presence: { allowEmpty: false, message: 'is required; ' }, format: { pattern: /^\d+(\.\d+)?$/, message: 'Numeric Field; ' }
	},
	tuesday: {
		presence: { allowEmpty: false, message: 'is required; ' }, format: { pattern: /^\d+(\.\d+)?$/, message: 'Numeric Field; ' }
	},
	wednesday: {
		presence: { allowEmpty: false, message: 'is required; ' }, format: { pattern: /^\d+(\.\d+)?$/, message: 'Numeric Field; ' }
	},
	thursday: {
		presence: { allowEmpty: false, message: 'is required; ' }, format: { pattern: /^\d+(\.\d+)?$/, message: 'Numeric Field; ' }
	},
	friday: {
		presence: { allowEmpty: false, message: 'is required; ' }, format: { pattern: /^\d+(\.\d+)?$/, message: 'Numeric Field; ' }
	}
};

function allocationPerDayIsValid(_formStateEricsson, _formStateNokia, totalOfEngineers) {
	const weekDays = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];

	const checkValues = (dia) => {
		const ericssonDayValue = _formStateEricsson.values[dia];
		const nokiaDayValue = _formStateNokia.values[dia];

		const newTotal = (parseFloat(ericssonDayValue) + parseFloat(nokiaDayValue));
		return (parseFloat(newTotal) <= parseFloat(totalOfEngineers));
	};

	for (let i = 0; i < weekDays.length; i++) {
		const dia = weekDays[i];
		const isValid = checkValues(dia);
		if (!isValid) {
			return isValid;
		}
	}

	return true;
}

function disableThisDay(day, teamDetails) {
	if (teamDetails.userAllowed) {
		return !(teamDetails.currentDay === day);
	}
	return true;
}

const AcceptanceManagementForm = () => {
	const classes = useStyles();
	const dispatch = useDispatch();

	const setFormStateNokia = formStateNokia => dispatch(SetFormStateNokia(formStateNokia));
	const setFormStateEricsson = formStateEricsson => dispatch(SetFormStateEricsson(formStateEricsson));

	const userId = useSelector(state => state.session.user.id);
	const formStateEricsson = useSelector(state => state.managementReducer.formStateEricsson);
	const formStateNokia = useSelector(state => state.managementReducer.formStateNokia);
	const teamDetails = useSelector(state => state.managementReducer.teamDetails);

	const _hasErrorNokia = (field) => (!!(formStateNokia.touched[field] && formStateNokia.errors[field]));
	const _hasErrorEricsson = (field) => (!!(formStateEricsson.touched[field] && formStateEricsson.errors[field]));

	useEffect(() => {
		dispatch(GetTeamAssign(userId));
		dispatch(GetTeamAssignDetails(userId));
	}, []);

	useMemo(() => {
		const errorsNokia = validate(formStateNokia.values, schema);

		setFormStateNokia({
			...formStateNokia,
			isValid: !errorsNokia,
			errors: errorsNokia || {},
		});
	}, [formStateNokia.values]);

	useMemo(() => {
		const errorsEricsson = validate(formStateEricsson.values, schema);

		setFormStateEricsson({
			...formStateEricsson,
			isValid: !errorsEricsson,
			errors: errorsEricsson || {},
		});
	}, [formStateEricsson.values]);

	const _handleChangeNokia = (event) => {
		setFormStateNokia({
			...formStateNokia,
			values: {
				...formStateNokia.values,
				[event.target.name]:
					event.target.type === 'checkbox'
						? event.target.checked
						: event.target.value,
			},
			touched: {
				...formStateNokia.touched,
				[event.target.name]: true,
			},
		});
	};

	const _handleChangeEricsson = (event) => {
		setFormStateEricsson({
			...formStateEricsson,
			values: {
				...formStateEricsson.values,
				[event.target.name]:
					event.target.type === 'checkbox'
						? event.target.checked
						: event.target.value,
			},
			touched: {
				...formStateEricsson.touched,
				[event.target.name]: true,
			},
		});
	};

	const reset = () => {
		dispatch(ResetAllocation(userId, teamDetails.id));
	};

	const save = () => {
		const allocationUpdate = {
			Ericsson: formStateEricsson.values,
			Nokia: formStateNokia.values,
			UserId: userId
		};

		if (allocationPerDayIsValid(formStateEricsson, formStateNokia, teamDetails.totalOfEngineers)) {
			dispatch(SaveAllocation(allocationUpdate));
		} else {
			const errorMessage = `The total resources allocated per day is ${teamDetails.totalOfEngineers}.`;

			dispatch(enqueueSnackbar({ message: errorMessage, options: { variant: 'error' } }));
		}
	};

	return (
		<Card className={classes.content}>
			<CardContent>
				<Grid container spacing={2}>
					<Grid item md={12} xs={12}>
						<Divider><Typography variant="h5" gutterBottom component="div">Ericsson</Typography></Divider>
					</Grid>
					<Grid item xs={1} />
					<Grid item xs={2}>
						<TextField
							fullWidth
							label="Monday"
							name="monday"
							defaultValue=""
							onChange={_handleChangeEricsson}
							value={formStateEricsson.values.monday || ''}
							variant="outlined"
							disabled={disableThisDay('monday', teamDetails)}
							error={_hasErrorEricsson('monday')}
							helperText={
								_hasErrorEricsson('monday') ? formStateEricsson.errors.monday : ''
							}
						/>
					</Grid>
					<Grid item xs={2}>
						<TextField
							fullWidth
							label="Tuesday"
							name="tuesday"
							defaultValue=""
							onChange={_handleChangeEricsson}
							value={formStateEricsson.values.tuesday || ''}
							variant="outlined"
							disabled={disableThisDay('tuesday', teamDetails)}
							error={_hasErrorEricsson('tuesday')}
							helperText={
								_hasErrorEricsson('tuesday') ? formStateEricsson.errors.tuesday : ''
							}
						/>
					</Grid>
					<Grid item xs={2}>
						<TextField
							fullWidth
							label="Wednesday"
							name="wednesday"
							defaultValue=""
							onChange={_handleChangeEricsson}
							value={formStateEricsson.values.wednesday || ''}
							variant="outlined"
							error={_hasErrorEricsson('wednesday')}
							helperText={
								_hasErrorEricsson('wednesday') ? formStateEricsson.errors.wednesday : ''
							}
							disabled={disableThisDay('wednesday', teamDetails)}
						/>
					</Grid>
					<Grid item xs={2}>
						<TextField
							fullWidth
							label="Thursday"
							name="thursday"
							defaultValue=""
							onChange={_handleChangeEricsson}
							value={formStateEricsson.values.thursday || ''}
							variant="outlined"
							error={_hasErrorEricsson('thursday')}
							helperText={
								_hasErrorEricsson('thursday') ? formStateEricsson.errors.thursday : ''
							}
							disabled={disableThisDay('thursday', teamDetails)}
						/>
					</Grid>
					<Grid item xs={2}>
						<TextField
							fullWidth
							label="Friday"
							name="friday"
							defaultValue=""
							onChange={_handleChangeEricsson}
							value={formStateEricsson.values.friday || ''}
							variant="outlined"
							error={_hasErrorEricsson('friday')}
							helperText={
								_hasErrorEricsson('friday') ? formStateEricsson.errors.friday : ''
							}
							disabled={disableThisDay('friday', teamDetails)}
						/>
					</Grid>
				</Grid>
				<br />
				<Grid container spacing={2}>
					<Grid item md={12} xs={12}>
						<Divider><Typography variant="h5" gutterBottom component="div">Nokia</Typography></Divider>
					</Grid>
					<Grid item xs={1} />
					<Grid item xs={2}>
						<TextField
							fullWidth
							label="Monday"
							name="monday"
							defaultValue=""
							onChange={_handleChangeNokia}
							value={formStateNokia.values.monday || ''}
							variant="outlined"
							error={_hasErrorNokia('monday')}
							helperText={
								_hasErrorNokia('monday') ? formStateNokia.errors.monday : ''
							}
							disabled={disableThisDay('monday', teamDetails)}
						/>
					</Grid>
					<Grid item xs={2}>
						<TextField
							fullWidth
							label="Tuesday"
							name="tuesday"
							defaultValue=""
							onChange={_handleChangeNokia}
							value={formStateNokia.values.tuesday || ''}
							variant="outlined"
							error={_hasErrorNokia('tuesday')}
							helperText={
								_hasErrorNokia('tuesday') ? formStateNokia.errors.tuesday : ''
							}
							disabled={disableThisDay('tuesday', teamDetails)}
						/>
					</Grid>
					<Grid item xs={2}>
						<TextField
							fullWidth
							label="Wednesday"
							name="wednesday"
							defaultValue=""
							onChange={_handleChangeNokia}
							value={formStateNokia.values.wednesday || ''}
							variant="outlined"
							error={_hasErrorNokia('wednesday')}
							helperText={
								_hasErrorNokia('wednesday') ? formStateNokia.errors.wednesday : ''
							}
							disabled={disableThisDay('wednesday', teamDetails)}
						/>
					</Grid>
					<Grid item xs={2}>
						<TextField
							fullWidth
							label="Thursday"
							name="thursday"
							defaultValue=""
							onChange={_handleChangeNokia}
							value={formStateNokia.values.thursday || ''}
							variant="outlined"
							error={_hasErrorNokia('thursday')}
							helperText={
								_hasErrorNokia('thursday') ? formStateNokia.errors.thursday : ''
							}
							disabled={disableThisDay('thursday', teamDetails)}
						/>
					</Grid>
					<Grid item xs={2}>
						<TextField
							fullWidth
							label="Friday"
							name="friday"
							defaultValue=""
							onChange={_handleChangeNokia}
							value={formStateNokia.values.friday || ''}
							variant="outlined"
							error={_hasErrorNokia('friday')}
							helperText={
								_hasErrorNokia('friday') ? formStateNokia.errors.friday : ''
							}
							disabled={disableThisDay('friday', teamDetails)}
						/>
					</Grid>
				</Grid>
				<br />
				<br />
				<Grid container spacing={2}>
					<Grid item md={6} xs={6}>
						<Divider><Typography variant="h5" gutterBottom component="div">Current Period</Typography></Divider>
					</Grid>
					<Grid item md={6} xs={6}>
						<Divider><Typography variant="h5" gutterBottom component="div">Last Modification</Typography></Divider>
					</Grid>
					<Grid item xs={3}>
						<TextField
							fullWidth
							label="Year"
							name="year"
							disabled
							defaultValue=""
							value={teamDetails.year || ''}
						/>
					</Grid>
					<Grid item xs={3}>
						<TextField
							fullWidth
							label="Week"
							name="week"
							disabled
							defaultValue=""
							value={teamDetails.week || ''}
						/>
					</Grid>
					<Grid item xs={3}>
						<TextField
							fullWidth
							label="User"
							name="userId"
							disabled
							defaultValue=""
							value={teamDetails.userName || ''}
						/>
					</Grid>
					<Grid item xs={3}>
						<TextField
							fullWidth
							label="Time"
							name="LastUpdate"
							disabled
							defaultValue=""
							value={teamDetails.lastUpdate || ''}
						/>
					</Grid>
				</Grid>
			</CardContent>
			<Divider />
			<CardActions>
				<Typography variant="h5" gutterBottom component="div">
					The total resources allocated per day is
					{' '}
					{teamDetails.totalOfEngineers}
				</Typography>
				<Button className={classes.reset} onClick={reset} variant="contained" disabled={!teamDetails.userAllowed || !formStateEricsson.isValid || !formStateNokia.isValid}>Reset</Button>
				<Button className={classes.save} onClick={save} variant="contained" disabled={!teamDetails.userAllowed || !formStateEricsson.isValid || !formStateNokia.isValid}>Save</Button>
			</CardActions>
		</Card>
	);
};

export default AcceptanceManagementForm;
